import type { Ref } from 'react';
import { plural } from '@frameio/i18n';
import { AlertDialog, AlertDialogButton, Text, theme } from '@frameio/vapor';
import TEXT from './generated/TEXT/DeleteWarningDialog';

type Props = {
  dismissRef: Ref<unknown>;
  isShowing: boolean;
  itemCount: number;
  onCancelClick: () => void;
  onDeleteClick: () => void;
  onExited: () => void;
  onRequestClose: () => void;
};

export const DeleteWarningDialog = ({
  dismissRef,
  isShowing,
  itemCount,
  onCancelClick,
  onDeleteClick,
  onExited,
  onRequestClose,
}: Props) => (
  <AlertDialog
    actions={
      <>
        <AlertDialogButton ref={dismissRef} onClick={onCancelClick}>
          {TEXT.cancel}
        </AlertDialogButton>
        <AlertDialogButton
          autoFocus={true}
          color="error"
          data-dd-action-name="asset.delete"
          data-testid="delete-asset-button"
          onClick={onDeleteClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              onDeleteClick();
            }
          }}
        >
          {TEXT.delete}
        </AlertDialogButton>
      </>
    }
    details={
      <Text
        style={{
          paddingBottom: theme.spaces[WARNING_PADDING_BOTTOM],
          fontSize: theme.fontSizes[70],
        }}
      >
        {TEXT.details}
      </Text>
    }
    isShowing={isShowing}
    leastDestructiveRef={dismissRef}
    onExited={onExited}
    onRequestClose={onRequestClose}
    title={TEXT.title(itemCount) as string}
    variant="notice"
  />
);

const WARNING_PADDING_BOTTOM = 4;

TEXT.DEFINE({
  cancel: 'Cancel',
  delete: 'Delete',
  details:
    'Deleted items can be recovered for 30 days before being permanently deleted.',
  title: (count = 0) =>
    plural(count, {
      one: 'Delete Asset?',
      other: `Delete ${count} Assets?`,
    }),
});
